import { useEffect, useState } from 'react';
import { Flex } from '../../2022-component-library';
import { colors } from '../../ui';
import { VF2_DOT_COLORS } from './PatientResponseSidebarVF2';
import { keyframes } from '@emotion/react';
import { visualFieldTwoDots } from '../../helpers/data';
import { useFormattedDots } from '../../helpers/visual-field-two-hooks/useFormattedDots';
import { useBreakpoints } from '../../helpers/useBreakpoints';

const blink = keyframes`
0% {
  opacity: 1
} 
50% {
  opacity: 0
}
100% {
  opacity: 1
}`;

const GraphLine = ({ horizontal }) => {
  return (
    <Flex
      width={horizontal ? '100%' : '1px'}
      backgroundColor={colors.slate[700]}
      height={horizontal ? '1px' : '100%'}
      position='absolute'
      left={horizontal ? 0 : '50%'}
      top={horizontal ? '50%' : 0}
      transform={horizontal ? 'translateY(-50%)' : 'translateX(-50%)'}
    />
  );
};

const VisualFieldMatrixVF2 = ({ testInProgress, testProgressData, eyeTested, results }) => {
  const [rawDotDataset, setRawDotDataset] = useState([]);
  const [allDots, setAllDots] = useState([]);
  const [lineLength, setLineLength] = useState(0);
  const { seenDots, missedOnceDots, notSeenDots, outOfBoundariesDots, activeDot } = testProgressData;
  const { sm } = useBreakpoints();
  const getDotData = useFormattedDots();

  useEffect(() => {
    const getLineLength = () => {
      const maxValue = 400;
      const responsiveValue = sm && !results ? 205 : window.innerWidth / (results ? 4 : 3.5);
      //re-examine & simplify this logic.
      // also bounded by the viewport height so the matrix fits the session panel (iPad landscape)
      return setLineLength(Math.min(responsiveValue, maxValue, Math.max(180, window.innerHeight * 0.36)));
    };

    getLineLength();

    window.addEventListener('resize', getLineLength);

    return () => {
      window.removeEventListener('resize', getLineLength);
    };
  }, [sm, results]);

  useEffect(() => {
    const { localDots } = getDotData(visualFieldTwoDots, eyeTested);
    setRawDotDataset(localDots);
  }, [eyeTested, getDotData]);

  const getDotColor = (dot) => {
    switch (true) {
      case dot.id === 'coord15-1.5' || dot.id === 'coord-15-1.5':
        //blindspot
        return '#000';
      case missedOnceDots?.some((missedDot) => missedDot.id === dot.id):
        return VF2_DOT_COLORS.missedOnce;
      case notSeenDots?.some((missedDot) => missedDot.id === dot.id):
        return VF2_DOT_COLORS.notSeen;
      case seenDots?.some((seenDot) => seenDot.id === dot.id):
        return VF2_DOT_COLORS.seen;
      case outOfBoundariesDots?.some((outOfBoundsDot) => outOfBoundsDot.id === dot.id):
        return '#F2E6E6';
      default:
        return VF2_DOT_COLORS.toBeDisplayed;
    }
  };

  useEffect(() => {
    const getActualPointOnGraph = (degreeX, degreeY) => {
      const getMaxValue = () => {
        const allCoords = rawDotDataset.flatMap((coordObj) => {
          return Object.values(coordObj).filter((coord) => {
            return Math.abs(coord);
          });
        });
        return Math.max(...allCoords);
      };
      const maximumDegree = getMaxValue();
      const halfLineLength = lineLength / 2;
      const sections = halfLineLength / maximumDegree;
      let x = Math.round(Math.abs((maximumDegree + degreeX) * sections));
      let y = Math.round(Math.abs((maximumDegree + degreeY) * sections));
      //on stroke test, this needs to be "Math.floor()". Investigate off-by-one errors.

      [x, y] = [x, y].map((coord) => {
        //add padding to extreme coordinates
        if (coord === 0) return (coord += lineLength * 0.01);
        if (coord === lineLength) return (coord -= lineLength * 0.01);
        return coord;
      });
      return { x, y };
    };

    const renderCoords = () => {
      const withoutDisabledBlindSpot = rawDotDataset.filter((dot) => !dot.blindspot || (dot.blindspot && !dot.disabled));
      const parsedDots = withoutDisabledBlindSpot?.map((coordSet) => {
        const newMatrixId = `coord${coordSet.degreeX}${coordSet.degreeY}`;
        const invertedY = coordSet.degreeY * -1;
        //invert because CSS Y positions & actual graph Y positions are opposite
        const { x, y } = getActualPointOnGraph(coordSet.degreeX, invertedY);
        return { id: newMatrixId, left: x, top: y };
      });

      return parsedDots;
    };
    setAllDots(renderCoords());
  }, [rawDotDataset, lineLength]);

  return (
    <Flex
      height={`${lineLength * 1.05}px`}
      width={`${lineLength * 1.05}px`}
      //this is a container so slightly bigger than the actual matrix
      border={`2px solid ${testInProgress ? colors.primary : colors.slate[500]}`}
      borderRadius='8px'
      backgroundColor='#fff'
      centered
    >
      <Flex centered height={`${lineLength}px`} width={`${lineLength}px`} position='relative'>
        <GraphLine horizontal />
        <GraphLine vertical />
        {allDots?.map((dot) => {
          return (
            <Flex
              key={dot.id}
              id={dot.id}
              height={`${lineLength * 0.03}px`}
              width={`${lineLength * 0.03}px`}
              borderRadius='50%'
              position='absolute'
              transform='translate(-50%, -50%)'
              animation={activeDot?.id === dot.id ? `${blink} 750ms infinite` : null}
              backgroundColor={getDotColor(dot)}
              style={{
                top: dot.top,
                left: dot.left,
              }}
            />
          );
        })}
      </Flex>
    </Flex>
  );
};

export default VisualFieldMatrixVF2;
